import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Users, GraduationCap, TrendingUp, TrendingDown, DollarSign, BookOpen,
  Award, Flame, Target, UserPlus, Calendar as CalendarIcon, ArrowUpRight, ArrowDownRight,
  Activity, BarChart3, Zap, MessageSquare, Bookmark,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, differenceInDays, startOfDay, endOfDay } from "date-fns";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

type Preset = "7" | "30" | "90" | "custom";

interface DateRange { from: Date; to: Date; }

interface Kpi {
  // Growth
  totalStudents: number; newStudents: number; newStudentsPrev: number;
  totalEnrollments: number; totalEnrollmentsPrev: number;
  approved: number; pending: number; rejected: number;
  referralSignups: number;
  // Engagement
  activeUsers: number; activeUsersPrev: number;
  avgStreak: number; topStreak: number;
  lessonsCompleted: number; lessonsCompletedPrev: number;
  examPassRate: number;
  bookmarks: number; qaCount: number;
  // Revenue
  revenue: number; revenuePrev: number;
  premiumEnrolls: number; premiumGroups: number;
  freeToPaidRate: number;
  // Charts
  signupTrend: { date: string; signups: number; enrollments: number }[];
  statusBreakdown: { name: string; value: number }[];
  topCourses: { title: string; enrollments: number; completion: number }[];
  topLessons: { title: string; completions: number }[];
  weeklyActivity: { day: string; completions: number }[];
}

const COLORS = ["hsl(var(--primary))", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#ec4899"];

const KpiDashboardTab = () => {
  const [preset, setPreset] = useState<Preset>("30");
  const [range, setRange] = useState<DateRange>({ from: subDays(new Date(), 30), to: new Date() });
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<Kpi | null>(null);

  const setPresetRange = (p: Preset) => {
    setPreset(p);
    if (p !== "custom") {
      const days = parseInt(p, 10);
      setRange({ from: subDays(new Date(), days), to: new Date() });
    }
  };

  useEffect(() => {
    const fetchKpi = async () => {
      setLoading(true);
      try {
        const from = startOfDay(range.from).toISOString();
        const to = endOfDay(range.to).toISOString();
        const days = Math.max(1, differenceInDays(range.to, range.from) + 1);
        const prevFrom = subDays(range.from, days).toISOString();
        const prevTo = subDays(range.to, days).toISOString();

        const [
          { count: totalStudents },
          { data: newProfiles },
          { data: prevProfiles },
          { data: enrolls },
          { data: prevEnrolls },
          { data: progress },
          { data: prevProgress },
          { data: streaks },
          { data: premiumEnrolls },
          { data: prevPremiumEnrolls },
          { data: premiumGroups },
          { data: courses },
          { data: lessons },
          { data: referrals },
          { count: bookmarksCount },
          { count: qaCount },
          { data: examAttempts },
        ] = await Promise.all([
          supabase.from("profiles").select("*", { count: "exact", head: true }),
          supabase.from("profiles").select("user_id, created_at").gte("created_at", from).lte("created_at", to),
          supabase.from("profiles").select("user_id").gte("created_at", prevFrom).lte("created_at", prevTo),
          supabase.from("student_enrollments").select("status, enrolled_at, course_id, user_id").gte("enrolled_at", from).lte("enrolled_at", to),
          supabase.from("student_enrollments").select("status, enrolled_at").gte("enrolled_at", prevFrom).lte("enrolled_at", prevTo),
          supabase.from("student_lesson_progress").select("user_id, lesson_id, exam_passed, completed_at, updated_at").gte("updated_at", from).lte("updated_at", to),
          supabase.from("student_lesson_progress").select("exam_passed, completed_at").gte("updated_at", prevFrom).lte("updated_at", prevTo),
          supabase.from("user_streaks").select("current_streak, longest_streak"),
          (supabase as any).from("premium_enrollments").select("group_id, enrolled_at, user_id").gte("enrolled_at", from).lte("enrolled_at", to),
          (supabase as any).from("premium_enrollments").select("group_id, enrolled_at").gte("enrolled_at", prevFrom).lte("enrolled_at", prevTo),
          (supabase as any).from("premium_groups").select("id, price_cents"),
          supabase.from("courses").select("id, title"),
          supabase.from("lessons").select("id, title, course_id"),
          (supabase as any).from("referrals").select("id, created_at").gte("created_at", from).lte("created_at", to),
          supabase.from("lesson_bookmarks").select("*", { count: "exact", head: true }).gte("created_at", from).lte("created_at", to),
          (supabase as any).from("lesson_qa").select("*", { count: "exact", head: true }).gte("created_at", from).lte("created_at", to),
          supabase.from("exam_attempts").select("passed").gte("created_at", from).lte("created_at", to),
        ]);

        const e = (enrolls as any[]) || [];
        const approved = e.filter((x) => x.status === "approved").length;
        const pending = e.filter((x) => x.status === "pending").length;
        const rejected = e.filter((x) => x.status === "rejected").length;

        // Trend buckets
        const trend: Record<string, { signups: number; enrollments: number }> = {};
        const trendDays = Math.min(days, 90);
        for (let i = trendDays - 1; i >= 0; i--) {
          const d = subDays(range.to, i);
          trend[format(d, "yyyy-MM-dd")] = { signups: 0, enrollments: 0 };
        }
        for (const p of (newProfiles as any[]) || []) {
          const k = p.created_at?.slice(0, 10);
          if (k && trend[k]) trend[k].signups++;
        }
        for (const en of e) {
          const k = en.enrolled_at?.slice(0, 10);
          if (k && trend[k]) trend[k].enrollments++;
        }

        // Active users (any progress activity in range)
        const activeUserIds = new Set(((progress as any[]) || []).map((p) => p.user_id));
        const activeUsers = activeUserIds.size;
        const prevProgressArr = (prevProgress as any[]) || [];
        const activeUsersPrev = new Set(prevProgressArr.map((p: any) => p.user_id)).size;

        // Streaks
        const streakArr = (streaks as any[]) || [];
        const avgStreak = streakArr.length ? streakArr.reduce((s, x) => s + (x.current_streak || 0), 0) / streakArr.length : 0;
        const topStreak = streakArr.reduce((m, x) => Math.max(m, x.longest_streak || 0), 0);

        // Lessons completed (passed exams)
        const completed = ((progress as any[]) || []).filter((p) => p.exam_passed).length;
        const completedPrev = prevProgressArr.filter((p: any) => p.exam_passed).length;

        // Exam pass rate
        const attempts = (examAttempts as any[]) || [];
        const passRate = attempts.length ? (attempts.filter((a) => a.passed).length / attempts.length) * 100 : 0;

        // Revenue
        const priceMap = new Map(((premiumGroups as any[]) || []).map((g) => [g.id, g.price_cents || 0]));
        const revenueCents = ((premiumEnrolls as any[]) || []).reduce((s, p) => s + (priceMap.get(p.group_id) || 0), 0);
        const prevRevenueCents = ((prevPremiumEnrolls as any[]) || []).reduce((s: number, p: any) => s + (priceMap.get(p.group_id) || 0), 0);

        // Free to paid conversion
        const newStudentsCount = ((newProfiles as any[]) || []).length;
        const premiumCount = ((premiumEnrolls as any[]) || []).length;
        const freeToPaidRate = newStudentsCount ? (premiumCount / newStudentsCount) * 100 : 0;

        // Top courses
        const courseTitles = new Map(((courses as any[]) || []).map((c) => [c.id, c.title]));
        const courseEnrolls: Record<string, number> = {};
        const courseCompletions: Record<string, number> = {};
        for (const en of e.filter((x) => x.status === "approved")) {
          const t = courseTitles.get(en.course_id) || "Unknown";
          courseEnrolls[t] = (courseEnrolls[t] || 0) + 1;
        }
        const lessonToCourse = new Map(((lessons as any[]) || []).map((l) => [l.id, l.course_id]));
        for (const p of ((progress as any[]) || []).filter((x) => x.exam_passed)) {
          const cid = lessonToCourse.get(p.lesson_id);
          const t = cid ? courseTitles.get(cid) : null;
          if (t) courseCompletions[t] = (courseCompletions[t] || 0) + 1;
        }
        const topCourses = Object.entries(courseEnrolls)
          .map(([title, enrollments]) => ({ title, enrollments, completion: courseCompletions[title] || 0 }))
          .sort((a, b) => b.enrollments - a.enrollments).slice(0, 5);

        // Top lessons by completion
        const lessonTitles = new Map(((lessons as any[]) || []).map((l) => [l.id, l.title]));
        const lessonCompletions: Record<string, number> = {};
        for (const p of ((progress as any[]) || []).filter((x) => x.exam_passed)) {
          const t = lessonTitles.get(p.lesson_id) || "Unknown";
          lessonCompletions[t] = (lessonCompletions[t] || 0) + 1;
        }
        const topLessons = Object.entries(lessonCompletions)
          .map(([title, completions]) => ({ title, completions }))
          .sort((a, b) => b.completions - a.completions).slice(0, 6);

        // Weekly activity (day of week)
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const weekly: Record<string, number> = Object.fromEntries(dayNames.map((d) => [d, 0]));
        for (const p of ((progress as any[]) || []).filter((x) => x.exam_passed && x.completed_at)) {
          const d = new Date(p.completed_at);
          weekly[dayNames[d.getDay()]]++;
        }

        setKpi({
          totalStudents: totalStudents || 0,
          newStudents: newStudentsCount,
          newStudentsPrev: ((prevProfiles as any[]) || []).length,
          totalEnrollments: e.length,
          totalEnrollmentsPrev: ((prevEnrolls as any[]) || []).length,
          approved, pending, rejected,
          referralSignups: ((referrals as any[]) || []).length,
          activeUsers, activeUsersPrev,
          avgStreak, topStreak,
          lessonsCompleted: completed, lessonsCompletedPrev: completedPrev,
          examPassRate: passRate,
          bookmarks: bookmarksCount || 0,
          qaCount: qaCount || 0,
          revenue: revenueCents / 100, revenuePrev: prevRevenueCents / 100,
          premiumEnrolls: premiumCount,
          premiumGroups: ((premiumGroups as any[]) || []).length,
          freeToPaidRate,
          signupTrend: Object.entries(trend).map(([date, v]) => ({ date: format(new Date(date), "MMM d"), ...v })),
          statusBreakdown: [
            { name: "Approved", value: approved },
            { name: "Pending", value: pending },
            { name: "Rejected", value: rejected },
          ].filter((x) => x.value > 0),
          topCourses, topLessons,
          weeklyActivity: dayNames.map((day) => ({ day, completions: weekly[day] })),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchKpi();
  }, [range]);

  const rangeLabel = useMemo(
    () => `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`,
    [range]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!kpi) return <p className="text-muted-foreground">No data available</p>;

  const pct = (cur: number, prev: number) => {
    if (!prev) return cur > 0 ? 100 : 0;
    return ((cur - prev) / prev) * 100;
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Header with controls */}
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-primary/10 via-background to-background backdrop-blur-xl p-6">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold">Analytics Overview</h2>
            </div>
            <p className="text-sm text-muted-foreground">{rangeLabel} • compared to previous period</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Tabs value={preset} onValueChange={(v) => setPresetRange(v as Preset)}>
              <TabsList className="bg-background/60 backdrop-blur">
                <TabsTrigger value="7">7d</TabsTrigger>
                <TabsTrigger value="30">30d</TabsTrigger>
                <TabsTrigger value="90">90d</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>
            </Tabs>
            {preset === "custom" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    Pick range
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={{ from: range.from, to: range.to }}
                    onSelect={(r) => r?.from && r?.to && setRange({ from: r.from, to: r.to })}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="growth" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full md:w-auto">
          <TabsTrigger value="growth" className="gap-1.5"><UserPlus className="w-4 h-4" />Growth</TabsTrigger>
          <TabsTrigger value="engagement" className="gap-1.5"><Flame className="w-4 h-4" />Engagement</TabsTrigger>
          <TabsTrigger value="content" className="gap-1.5"><BookOpen className="w-4 h-4" />Content</TabsTrigger>
          <TabsTrigger value="revenue" className="gap-1.5"><DollarSign className="w-4 h-4" />Revenue</TabsTrigger>
        </TabsList>

        {/* GROWTH */}
        <TabsContent value="growth" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Total Students" value={kpi.totalStudents} icon={Users} accent="primary" />
            <KpiCard label="New Signups" value={kpi.newStudents} delta={pct(kpi.newStudents, kpi.newStudentsPrev)} icon={UserPlus} accent="emerald" />
            <KpiCard label="New Enrollments" value={kpi.totalEnrollments} delta={pct(kpi.totalEnrollments, kpi.totalEnrollmentsPrev)} icon={GraduationCap} accent="blue" />
            <KpiCard label="Referral Signups" value={kpi.referralSignups} icon={Zap} accent="amber" />
          </div>

          <ChartCard title="Signups & Enrollments" icon={BarChart3}>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={kpi.signupTrend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="signups" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="enrollments" stroke="#10b981" strokeWidth={2} fill="url(#g2)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid gap-6 md:grid-cols-2">
            <ChartCard title="Enrollment Status" icon={Target}>
              {kpi.statusBreakdown.length === 0 ? (
                <EmptyState text="No enrollment data" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={kpi.statusBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={55} outerRadius={90} paddingAngle={3}
                      label={(e) => `${e.name}: ${e.value}`}>
                      {kpi.statusBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <Card className="border-border/50 bg-gradient-to-br from-card to-card/50 backdrop-blur">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-primary" />Funnel Snapshot</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <FunnelStep label="Signups" value={kpi.newStudents} total={kpi.newStudents} />
                <FunnelStep label="Enrolled" value={kpi.totalEnrollments} total={Math.max(kpi.newStudents, 1)} />
                <FunnelStep label="Approved" value={kpi.approved} total={Math.max(kpi.totalEnrollments, 1)} />
                <FunnelStep label="Premium" value={kpi.premiumEnrolls} total={Math.max(kpi.newStudents, 1)} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ENGAGEMENT */}
        <TabsContent value="engagement" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Active Users" value={kpi.activeUsers} delta={pct(kpi.activeUsers, kpi.activeUsersPrev)} icon={Activity} accent="primary" />
            <KpiCard label="Avg Streak" value={kpi.avgStreak.toFixed(1)} icon={Flame} accent="amber" suffix=" days" />
            <KpiCard label="Top Streak" value={kpi.topStreak} icon={Award} accent="emerald" suffix=" days" />
            <KpiCard label="Exam Pass Rate" value={`${kpi.examPassRate.toFixed(0)}%`} icon={Target} accent="blue" />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <ChartCard title="Activity by Day of Week" icon={BarChart3}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={kpi.weeklyActivity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="completions" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="grid grid-cols-1 gap-4">
              <KpiCard label="Bookmarks Added" value={kpi.bookmarks} icon={Bookmark} accent="indigo" large />
              <KpiCard label="Q&A Posts" value={kpi.qaCount} icon={MessageSquare} accent="pink" large />
            </div>
          </div>
        </TabsContent>

        {/* CONTENT */}
        <TabsContent value="content" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard label="Lessons Completed" value={kpi.lessonsCompleted} delta={pct(kpi.lessonsCompleted, kpi.lessonsCompletedPrev)} icon={BookOpen} accent="primary" />
            <KpiCard label="Exam Pass Rate" value={`${kpi.examPassRate.toFixed(0)}%`} icon={Target} accent="emerald" />
            <KpiCard label="Bookmarks" value={kpi.bookmarks} icon={Bookmark} accent="amber" />
          </div>

          <ChartCard title="Top Courses by Enrollment" icon={Award}>
            {kpi.topCourses.length === 0 ? <EmptyState text="No enrollments yet" /> : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={kpi.topCourses} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis type="category" dataKey="title" stroke="hsl(var(--muted-foreground))" fontSize={11} width={140} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="enrollments" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} name="Enrollments" />
                  <Bar dataKey="completion" fill="#10b981" radius={[0, 6, 6, 0]} name="Completions" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Top Lessons by Completion" icon={BookOpen}>
            {kpi.topLessons.length === 0 ? <EmptyState text="No completions yet" /> : (
              <div className="space-y-2">
                {kpi.topLessons.map((l, i) => {
                  const max = kpi.topLessons[0].completions || 1;
                  const pctW = (l.completions / max) * 100;
                  return (
                    <div key={i} className="group">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="truncate flex-1 mr-2">{i + 1}. {l.title}</span>
                        <Badge variant="secondary" className="shrink-0">{l.completions}</Badge>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all"
                          style={{ width: `${pctW}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ChartCard>
        </TabsContent>

        {/* REVENUE */}
        <TabsContent value="revenue" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Revenue" value={`$${kpi.revenue.toFixed(2)}`} delta={pct(kpi.revenue, kpi.revenuePrev)} icon={DollarSign} accent="emerald" />
            <KpiCard label="Premium Enrollments" value={kpi.premiumEnrolls} icon={Award} accent="primary" />
            <KpiCard label="Active Groups" value={kpi.premiumGroups} icon={Users} accent="blue" />
            <KpiCard label="Free → Paid" value={`${kpi.freeToPaidRate.toFixed(1)}%`} icon={TrendingUp} accent="amber" />
          </div>

          <ChartCard title="Revenue Snapshot" icon={DollarSign}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
              <RevTile label="This period" value={`$${kpi.revenue.toFixed(2)}`} highlight />
              <RevTile label="Previous period" value={`$${kpi.revenuePrev.toFixed(2)}`} />
              <RevTile label="Avg per signup"
                value={`$${(kpi.newStudents ? kpi.revenue / kpi.newStudents : 0).toFixed(2)}`} />
            </div>
          </ChartCard>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0.75rem",
  fontSize: 12,
  boxShadow: "0 10px 30px -10px hsl(var(--primary) / 0.2)",
};

const accentMap: Record<string, string> = {
  primary: "from-primary/20 to-primary/5 text-primary",
  emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-600 dark:text-emerald-400",
  amber: "from-amber-500/20 to-amber-500/5 text-amber-600 dark:text-amber-400",
  blue: "from-blue-500/20 to-blue-500/5 text-blue-600 dark:text-blue-400",
  indigo: "from-indigo-500/20 to-indigo-500/5 text-indigo-600 dark:text-indigo-400",
  pink: "from-pink-500/20 to-pink-500/5 text-pink-600 dark:text-pink-400",
};

const KpiCard = ({ label, value, delta, icon: Icon, accent = "primary", suffix = "", large = false }: any) => (
  <Card className={`relative overflow-hidden border-border/50 bg-gradient-to-br ${accentMap[accent]} backdrop-blur-xl hover:shadow-lg transition-shadow`}>
    <CardContent className={large ? "p-5" : "p-4"}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-xl bg-background/60 backdrop-blur flex items-center justify-center ${accentMap[accent].split(" ").pop()}`}>
          <Icon className="w-4 h-4" />
        </div>
        {typeof delta === "number" && (
          <div className={`flex items-center gap-0.5 text-xs font-semibold ${delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
            {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta).toFixed(0)}%
          </div>
        )}
      </div>
      <p className={`font-bold leading-none text-foreground ${large ? "text-3xl" : "text-2xl"}`}>{value}{suffix}</p>
      <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, icon: Icon, children }: any) => (
  <Card className="border-border/50 bg-gradient-to-br from-card to-card/50 backdrop-blur-xl">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />{title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const FunnelStep = ({ label, value, total }: any) => {
  const w = total ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">{value} <span className="text-xs">({w.toFixed(0)}%)</span></span>
      </div>
      <div className="h-2.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-gradient-to-r from-primary to-primary/50 transition-all" style={{ width: `${Math.min(w, 100)}%` }} />
      </div>
    </div>
  );
};

const RevTile = ({ label, value, highlight = false }: any) => (
  <div className={`rounded-xl p-4 border ${highlight ? "border-primary/30 bg-primary/5" : "border-border bg-muted/30"}`}>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`text-2xl font-bold ${highlight ? "text-primary" : ""}`}>{value}</p>
  </div>
);

const EmptyState = ({ text }: { text: string }) => (
  <p className="text-sm text-muted-foreground py-12 text-center">{text}</p>
);

export default KpiDashboardTab;
